/**
 * Stalwart Mail Server — multi-instance container modul.
 *
 * Kompletní správa: deploy, start/stop/restart, správa účtů/domén/DNS/fronty.
 * Per-instance detail s tabs: Účty | Domény | DNS | Fronta | Connection.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Globe2,
  Inbox,
  Loader2,
  Mail,
  Play,
  Plus,
  RefreshCw,
  Server,
  Square,
  Trash2,
  XCircle,
  UserPlus,
  Users,
  Shield,
  Inbox,
  Settings,
  KeyRound,
  ExternalLink,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Modal,
  PageHeader,
  PublishPanel,
  SecretField,
  StatCard,
  StatsGrid,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  UrlRow,
} from '../../components/ui';
import { api } from '../../lib/api';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Types ──────────────────────────────────────────────────────────────────

interface InstanceListItem {
  name: string;
  db_status: 'provisioning' | 'running' | 'stopped' | 'error' | 'deleting';
  docker_status: 'running' | 'stopped' | 'missing';
  image: string;
  ports: Record<string, number>;
  hostname: string | null;
  domain: string | null;
}

interface InstanceDetail extends InstanceListItem {
  drift: boolean;
  api_health: { ok: boolean; ms: number; status?: number; error?: string } | null;
  created_at: string;
  updated_at: string;
  error_message: string | null;
}

interface Credential {
  ok: boolean;
  admin?: { user: string; password: string; url: string };
  ports?: Record<string, number>;
  private_endpoints?: Record<string, string>;
  hostname?: string | null;
  error?: string;
}

interface Account {
  name: string;
  type: string;
  description?: string;
  emails: string[];
  quota: number;
  quota_human: string;
  memberOf?: string[];
  enabledPermissions?: string[];
  disabledPermissions?: string[];
  enabled: boolean;
}

interface Domain {
  name: string;
  type: string;
  description?: string;
  enabled: boolean;
}

interface QueueMessage {
  id: string;
  created: string;
  size: number;
  size_human: string;
  from: string;
  domains: Array<{
    domain: string;
    recipients: Array<{ address: string; status: string }>;
    recipient_count: number;
    failed: number;
  }>;
  total_recipients: number;
}

interface QueueResponse {
  ok: boolean;
  instance: string;
  total: number;
  page: number;
  limit: number;
  messages: QueueMessage[];
  summary: { total_messages: number; failed: number; deferred: number; pending: number };
}

interface DnsRecord {
  type: 'MX' | 'TXT' | 'SRV';
  name: string;
  data: string;
  ttl: number;
  description: string;
}

interface DnsResponse {
  ok: boolean;
  instance: string;
  domain: string;
  mail_host: string;
  public_host: string;
  dkim_resolved: boolean;
  records: DnsRecord[];
}

interface NetworkPublishEntry {
  url: string;
  service: string;
  instance_ref?: string;
  desired: boolean;
  actual: boolean;
}

interface ConnectionInfo {
  ok: boolean;
  instance: string;
  admin: { url: string; user: string; password_hint: string };
  ports: Record<string, number>;
  imap: { starttls: string; ssl: string; ssl_description: string };
  smtp: { starttls: string; ssl: string; relay: string; starttls_description: string; ssl_description: string };
  jmap: { url: string };
  webmail?: { path: string; note?: string };
  hostname: string | null;
  domain: string | null;
}

// ── Deploy Modal ───────────────────────────────────────────────────────────

function DeployModal({ onClose, onDeployed }: { onClose: () => void; onDeployed: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; admin_password?: string }>('stalwart.deploy', {
        name,
        hostname: hostname || undefined,
        start: true,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('stalwart.deployStartedToast', { name, password: r.admin_password }));
        onDeployed();
        onClose();
      } else {
        toast.error(r.error ?? t('stalwart.deployFailedToast'));
      }
    },
    onError: (e: Error) => toast.error(t('stalwart.deployErrorToast', { message: e.message })),
  });

  return (
    <Modal open={true} onClose={onClose} title={t('stalwart.newInstanceModalTitle')} size='md'>
      <div className='space-y-4'>
        <div>
          <Label>{t('stalwart.instanceSlugLabel')}</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('stalwart.instanceSlugPlaceholder')}
            autoFocus
          />
        </div>
        <div>
          <Label>{t('stalwart.hostnameOptionalLabel')}</Label>
          <Input
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder={t('stalwart.hostnamePlaceholder')}
          />
          <p className='text-[11px] text-gray-500 mt-1'>
            {t('stalwart.hostnameDefaultHint')}
          </p>
        </div>
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='ghost' onClick={onClose}>{t('stalwart.cancelButton')}</Button>
          <Button
            variant='primary'
            disabled={!name || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? t('stalwart.deployingLabel') : t('stalwart.deployButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Credentials Modal ─────────────────────────────────────────────────────

function CredentialsModal({ name, onClose }: { name: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<Credential>({
    queryKey: ['stalwart-credentials', name],
    queryFn: () => api.mcp<Credential>('stalwart.credentials', { name }),
  });

  return (
    <Modal open={true} onClose={onClose} title={t('stalwart.credentialsModalTitle', { name })} size='lg'>
      {isLoading && <div className='py-4 text-center text-sm text-gray-500'>{t('stalwart.loadingLabel')}</div>}
      {data && !data.ok && <div className='text-sm text-amber-400'>{data.error}</div>}
      {data?.ok && (
        <div className='space-y-4'>
          <Card>
            <CardContent className='p-4 space-y-2'>
              <div className='text-xs uppercase opacity-60 mb-2'>{t('stalwart.adminAccessLabel')}</div>
              <UrlRow label={t('stalwart.urlLabel')} url={data.admin?.url} isPublic />
              <SecretField label={t('stalwart.userLabel')} value={data.admin?.user} />
              <SecretField label={t('stalwart.passwordLabel')} value={data.admin?.password} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className='p-4 space-y-2'>
              <div className='text-xs uppercase opacity-60 mb-2'>{t('stalwart.connectionEndpointsLabel')}</div>
              {data.private_endpoints && Object.entries(data.private_endpoints).map(([k, v]) => (
                <UrlRow key={k} label={k.toUpperCase()} url={v} />
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </Modal>
  );
}

// ── Account Modal ─────────────────────────────────────────────────────────

function AccountModal({
  name,
  existingEmails,
  onClose,
  onSuccess,
}: {
  name: string;
  existingEmails?: string[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(existingEmails?.[0] ?? '');
  const [password, setPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [quota, setQuota] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{
        ok: boolean;
        error?: string;
        password?: string;
        principal?: { password?: string };
      }>('stalwart.create_principal', {
        name,
        principal_name: email,
        type: 'individual',
        emails: [email],
        password: password || undefined,
        description: description || undefined,
        quota: quota ? Number(quota) : undefined,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        const pwd = r.principal?.password || r.password || '—';
        setGeneratedPassword(pwd);
        toast.info(t('stalwart.accountCreatedToast', { email, password: pwd }));
        onSuccess();
      } else {
        toast.error(r.error ?? t('stalwart.createFailedToast'));
      }
    },
    onError: (e: Error) => toast.error(t('stalwart.createErrorToast', { message: e.message })),
  });

  return (
    <Modal open={true} onClose={onClose} title={t('stalwart.newAccountModalTitle')} size='md'>
      <div className='space-y-4'>
        {generatedPassword ? (
          <div className='bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-3 space-y-1'>
            <div className='text-xs text-emerald-400 font-semibold'>{t('stalwart.accountCreatedTitle')}</div>
            <div className='text-xs text-gray-400'>{t('stalwart.emailLabel')} <span className='text-white font-mono'>{email}</span></div>
            <div className='text-xs text-gray-400'>{t('stalwart.passwordColonLabel')} <span className='text-white font-mono'>{generatedPassword}</span></div>
            <p className='text-[10px] text-amber-300/80 mt-2'>{t('stalwart.passwordWarningHint')}</p>
            <Button variant='primary' size='sm' className='mt-2 w-full' onClick={onClose}>
              {t('stalwart.closeButton')}
            </Button>
          </div>
        ) : (
          <>
            <div>
              <Label>{t('stalwart.emailAccountLabel')}</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('stalwart.emailPlaceholder')}
                autoFocus
              />
            </div>
            <div>
              <Label>{t('stalwart.passwordRandomLabel')}</Label>
              <Input
                type='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder='••••••••'
              />
              <p className='text-[10px] text-gray-500 mt-1'>{t('stalwart.emptyRandomHint')}</p>
            </div>
            <div>
              <Label>{t('stalwart.descriptionLabel')}</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('stalwart.namePlaceholderExample')}
              />
            </div>
            <div>
              <Label>{t('stalwart.quotaLabel')}</Label>
              <Input
                type='number'
                value={quota}
                onChange={(e) => setQuota(e.target.value)}
                placeholder='1024'
              />
            </div>
            <div className='flex justify-end gap-2 pt-2'>
              <Button variant='ghost' onClick={onClose}>{t('stalwart.cancelButton')}</Button>
              <Button
                variant='primary'
                disabled={!email || mut.isPending}
                onClick={() => mut.mutate()}
              >
                {mut.isPending ? t('stalwart.creatingLabel') : t('stalwart.createButton')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Domain Modal ──────────────────────────────────────────────────────────

function DomainModal({
  name,
  existingDomain,
  onClose,
  onSuccess,
}: {
  name: string;
  existingDomain?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState(existingDomain ?? '');
  const [description, setDescription] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string }>('stalwart.create_principal', {
        name,
        principal_name: domain,
        type: 'domain',
        description: description || undefined,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('stalwart.domainCreatedToast', { domain }));
        onSuccess();
        onClose();
      } else {
        toast.error(r.error ?? t('stalwart.createFailedToast'));
      }
    },
    onError: (e: Error) => toast.error(t('stalwart.createErrorToast', { message: e.message })),
  });

  return (
    <Modal open={true} onClose={onClose} title={existingDomain ? t('stalwart.editDomainModalTitle', { domain: existingDomain }) : t('stalwart.newDomainModalTitle')} size='md'>
      <div className='space-y-4'>
        <div>
          <Label>{t('stalwart.domainFqdnLabel')}</Label>
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder={t('stalwart.domainPlaceholder')}
            autoFocus
          />
        </div>
        <div>
          <Label>{t('stalwart.descriptionLabel')}</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('stalwart.domainDescriptionPlaceholder')}
          />
        </div>
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='ghost' onClick={onClose}>{t('stalwart.cancelButton')}</Button>
          <Button
            variant='primary'
            disabled={!domain || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? t('stalwart.savingPluralLabel') : t('stalwart.saveButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Account Detail Modal ──────────────────────────────────────────────────

function AccountDetailModal({
  account,
  instanceName,
  onClose,
  onRefresh,
}: {
  account: Account;
  instanceName: string;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(account.description ?? '');
  const [quota, setQuota] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const deleteMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string }>('stalwart.delete_principal', {
        name: instanceName,
        principal_name: account.name,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('stalwart.accountDeletedToast', { name: account.name }));
        onRefresh();
        onClose();
      } else {
        toast.error(r.error ?? t('stalwart.deleteFailedToast'));
      }
    },
  });

  const updateMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string }>('stalwart.update_principal', {
        name: instanceName,
        principal_name: account.name,
        description,
        quota: quota ? Number(quota) : 0,
        password: newPassword || undefined,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('stalwart.accountUpdatedToast', { name: account.name }));
        onRefresh();
        setEditing(false);
      } else {
        toast.error(r.error ?? t('stalwart.updateFailedToast'));
      }
    },
  });

  return (
    <Modal open={true} onClose={onClose} title={t('stalwart.accountModalTitle', { name: account.name })} size='lg'>
      {deleteMut.isPending ? (
        <div className='py-4 text-center text-sm text-gray-500'>{t('stalwart.deletingLabel')}</div>
      ) : (
        <div className='space-y-4'>
          {/* Info */}
          <Card>
            <CardContent className='p-4 space-y-2'>
              <div className='text-xs uppercase opacity-60 mb-2'>{t('stalwart.infoLabel')}</div>
              <div className='grid grid-cols-2 gap-2 text-sm'>
                <div>
                  <span className='text-gray-500'>{t('stalwart.emailColonLabel')}</span> {account.emails.join(', ')}
                </div>
                <div>
                  <span className='text-gray-500'>{t('stalwart.quotaColonLabel')}</span> {account.quota_human}
                </div>
                <div>
                  <span className='text-gray-500'>{t('stalwart.typeColonLabel')}</span> {account.type}
                </div>
                <div>
                  <span className='text-gray-500'>{t('stalwart.statusColonLabel')}</span>{' '}
                  <StatusDot color={account.enabled ? 'green' : 'gray'} />
                  {account.enabled ? t('stalwart.activeLabel') : t('stalwart.disabledLabel')}
                </div>
              </div>
              {account.description && (
                <div className='text-sm text-gray-400'>
                  <span className='text-gray-500'>{t('stalwart.descriptionColonLabel')}</span> {account.description}
                </div>
              )}
              {account.memberOf && account.memberOf.length > 0 && (
                <div className='text-sm'>
                  <span className='text-gray-500'>{t('stalwart.groupsColonLabel')}</span>{' '}
                  {account.memberOf.map((g) => <Badge key={g} variant='blue' className='ml-1 text-[10px]'>{g}</Badge>)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit */}
          <Card>
            <CardContent className='p-4 space-y-3'>
              <div className='flex justify-between items-center'>
                <div className='text-xs uppercase opacity-60'>{t('stalwart.editingLabel')}</div>
                <Button variant='ghost' onClick={() => setEditing(!editing)}>
                  {editing ? t('stalwart.cancelButton') : t('stalwart.editButton')}
                </Button>
              </div>
              {editing && (
                <div className='space-y-3'>
                  <div>
                    <Label>{t('stalwart.descriptionLabel')}</Label>
                    <Input value={description} onChange={(e) => setDescription(e.target.value)} />
                  </div>
                  <div>
                    <Label>{t('stalwart.quotaLabel')}</Label>
                    <Input type='number' value={quota} onChange={(e) => setQuota(e.target.value)} placeholder='1024' />
                  </div>
                  <div>
                    <Label>{t('stalwart.newPasswordLabel')}</Label>
                    <Input
                      type='password'
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                  <Button
                    variant='primary'
                    size='sm'
                    disabled={updateMut.isPending}
                    onClick={() => updateMut.mutate()}
                  >
                    {updateMut.isPending ? t('stalwart.savingLabel') : t('stalwart.saveButton')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className='flex justify-end gap-2'>
            <Button variant='ghost' onClick={onClose}>{t('stalwart.closeButton')}</Button>
            <Button
              variant='danger'
              disabled={deleteMut.isPending}
              onClick={async () => {
                if (await themedConfirm(t('stalwart.deleteAccountConfirm', { name: account.name }))) deleteMut.mutate();
              }}
            >
              {deleteMut.isPending ? t('stalwart.deletingSingularLabel') : t('stalwart.deleteAccountButton')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── DNS Records Modal ─────────────────────────────────────────────────────

function DnsRecordsModal({
  instanceName,
  domain,
  onClose,
}: {
  instanceName: string;
  domain: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useQuery<DnsResponse>({
    queryKey: ['stalwart-dns', instanceName, domain],
    queryFn: () =>
      api.mcp<DnsResponse>('stalwart.dns_records', {
        name: instanceName,
        domain,
      }),
  });

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    toast.success(t('stalwart.copiedToClipboardToast'));
  }

  const applyMut = useMutation({
    mutationFn: () =>
      api.mcp<{
        ok: boolean;
        error?: string;
        applied?: Array<{ name: string; ok: boolean; error?: string }>;
      }>('stalwart.dns_records', {
        name: instanceName,
        domain,
        apply: true,
      }),

    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('stalwart.dnsRecordsCreatedToast'));
        refetch();
      } else {
        toast.error(r.error ?? t('stalwart.dnsCreateFailedToast'));
      }
    },
  });

  return (
    <Modal open={true} onClose={onClose} title={t('stalwart.dnsRecordsModalTitle', { domain })} size='lg'>
      {isLoading ? (
        <div className='py-4 text-center text-sm text-gray-500'>
          <Loader2 className='inline animate-spin mr-2' size={14} />{t('stalwart.loadingLabel')}
        </div>
      ) : data?.ok ? (
        <div className='space-y-4'>
          {/* Summary */}
          <div className='grid grid-cols-2 gap-2 text-sm'>
            <div><span className='text-gray-500'>{t('stalwart.mailHostLabel')}</span> {data.mail_host}</div>
            <div><span className='text-gray-500'>{t('stalwart.publicHostLabel')}</span> {data.public_host}</div>
            <div><span className='text-gray-500'>{t('stalwart.dkimLabel')}</span>{' '}
              <Badge variant={data.dkim_resolved ? 'green' : 'warning'} className='ml-1 text-[10px]'>
                {data.dkim_resolved ? t('stalwart.dkimFoundLabel') : t('stalwart.dkimPlaceholderLabel')}
              </Badge>
            </div>
          </div>

          {/* Records table */}
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-zinc-700'>
                  <th className='text-left py-2 px-2 text-xs uppercase opacity-60'>{t('stalwart.colType')}</th>
                  <th className='text-left py-2 px-2 text-xs uppercase opacity-60'>{t('stalwart.colName2')}</th>
                  <th className='text-left py-2 px-2 text-xs uppercase opacity-60'>{t('stalwart.colData')}</th>
                  <th className='text-left py-2 px-2 text-xs uppercase opacity-60'>{t('stalwart.colTtl')}</th>
                  <th className='text-left py-2 px-2 text-xs uppercase opacity-60'>{t('stalwart.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((r, i) => (
                  <tr key={i} className='border-b border-zinc-800/50 hover:bg-zinc-800/30'>
                    <td className='py-2 px-2'>
                      <Badge variant='blue' className='text-[10px]'>{r.type}</Badge>
                    </td>
                    <td className='py-2 px-2 font-mono text-xs'>{r.name}</td>
                    <td className='py-2 px-2 font-mono text-xs break-all text-gray-300'>{r.data}</td>
                    <td className='py-2 px-2 text-xs'>{r.ttl}s</td>
                    <td className='py-2 px-2'>
                      <Button variant='ghost' size='icon' onClick={() => copyToClipboard(`${r.name}\t${r.ttl}\t${r.type}\t${r.data}`)}>
                        <Copy size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Apply button */}
          <div className='flex justify-between items-center pt-2'>
            <p className='text-xs text-gray-500'>
              {t('stalwart.autoCreateDnsHint')}
            </p>
            <Button
              variant='primary'
              size='sm'
              disabled={applyMut.isPending}
              onClick={() => applyMut.mutate()}
            >
              {applyMut.isPending ? t('stalwart.creatingSingularLabel') : t('stalwart.createDnsRecordsButton')}
            </Button>
          </div>
        </div>
      ) : (
        <div className='text-sm text-amber-400'>{data?.error ?? t('stalwart.genericErrorLabel')}</div>
      )}
    </Modal>
  );
}

// ── Instance Detail View ──────────────────────────────────────────────────

function InstanceDetailView({
  inst,
  onClose,
}: {
  inst: InstanceDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('accounts');
  const [searchFilter, setSearchFilter] = useState('');
  const [accountModalFor, setAccountModalFor] = useState<'create' | string | null>(null);
  const [domainModalFor, setDomainModalFor] = useState<'create' | string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [dnsForDomain, setDnsForDomain] = useState<string | null>(null);
  const [queuePage, setQueuePage] = useState(1);

  // ── Instance status ──
  const { data: statusData } = useQuery({
    queryKey: ['stalwart-status', inst.name],
    queryFn: () => api.mcp<InstanceDetail>('stalwart.status', { name: inst.name }),
    refetchInterval: 10_000,
  });

  // ── Accounts ──
  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['stalwart-accounts', inst.name, searchFilter],
    queryFn: () =>
      api.mcp<{ ok: boolean; accounts?: Account[]; total: number }>('stalwart.list_accounts', {
        name: inst.name,
        filter: searchFilter || undefined,
      }),
  });

  // ── Domains ──
  const { data: domainsData, isLoading: loadingDomains } = useQuery({
    queryKey: ['stalwart-domains', inst.name],
    queryFn: () =>
      api.mcp<{ ok: boolean; domains?: Domain[]; total: number }>('stalwart.list_domains', {
        name: inst.name,
      }),
  });

  // ── Queue ──
  const { data: queueData, isLoading: loadingQueue } = useQuery({
    queryKey: ['stalwart-queue', inst.name, queuePage],
    queryFn: () =>
      api.mcp<QueueResponse>('stalwart.queue_status', {
        name: inst.name,
        page: queuePage,
        limit: 20,
      }),
  });

  // ── Connection Info ──
  const { data: connData } = useQuery({
    queryKey: ['stalwart-connection', inst.name],
    queryFn: () => api.mcp<ConnectionInfo>('stalwart.connection_info', { name: inst.name }),
  });

  // ── Publish status (network catalog — zdroj pravdy pro "je to publikované?") ──
  const { data: publishData } = useQuery({
    queryKey: ['stalwart-publish-status', inst.name],
    queryFn: () => api.mcp<{ ok: boolean; entries: NetworkPublishEntry[] }>('network.list', { service: 'stalwart' }),
    refetchInterval: 10_000,
  });
  const publishEntry = publishData?.entries?.find((e) => e.instance_ref === inst.name);
  const publicAdminUrl = publishEntry?.url ? `https://${publishEntry.url}` : null;

  // ── Manage actions ──
  async function manageInstance(action: 'start' | 'stop' | 'restart') {
    const r = await api.mcp<{ ok: boolean; error?: string }>('stalwart.manage', {
      name: inst.name,
      action,
    });
    if (r.ok) toast.success(t('stalwart.instanceActionSuccessToast', { name: inst.name, action }));
    else toast.error(r.error ?? t('stalwart.actionFailedToast', { action }));
    qc.invalidateQueries({ queryKey: ['stalwart-status', inst.name] });
  }

  async function queueAction(messageId: string, action: 'retry' | 'cancel') {
    const r = await api.mcp<{ ok: boolean; error?: string }>('stalwart.queue_action', {
      name: inst.name,
      message_id: messageId,
      action,
    });
    if (r.ok) {
      toast.success(t('stalwart.queueActionToast', { action, messageId }));
      qc.invalidateQueries({ queryKey: ['stalwart-queue', inst.name] });
    } else {
      toast.error(r.error ?? t('stalwart.actionFailedGenericToast'));
    }
  }

  const status = statusData ?? inst;
  const accounts = accountsData?.accounts ?? [];
  const domains = domainsData?.domains ?? [];
  const queue = queueData?.messages ?? [];
  const queueSummary = queueData?.summary;

  // ── Status indicator ──
  const drift = (status.db_status === 'running' && status.docker_status !== 'running');
  const statusColor = drift
    ? 'text-amber-400'
    : status.db_status === 'running' && status.docker_status === 'running'
    ? 'text-emerald-400'
    : status.db_status === 'error'
    ? 'text-red-400'
    : 'text-gray-500';

  // ── Tab: Accounts ──
  function renderAccounts() {
    return (
      <div className='space-y-3'>
        {/* Toolbar */}
        <div className='flex justify-between items-center'>
          <div className='relative flex-1 max-w-xs'>
            <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500' size={14} />
            <Input
              placeholder={t('stalwart.searchAccountPlaceholder')}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className='pl-8'
            />
          </div>
          <Button variant='primary' size='sm' onClick={() => setAccountModalFor('create')}>
            <UserPlus size={13} /> {t('stalwart.newAccountButton')}
          </Button>
        </div>

        {/* Accounts list */}
        {loadingAccounts && (
          <div className='py-8 text-center text-sm text-gray-500'>
            <Loader2 className='inline animate-spin mr-2' size={14} />{t('stalwart.loadingAccountsLabel')}
          </div>
        )}

        {!loadingAccounts && accounts.length === 0 && (
          <EmptyState
            icon={<Users size={32} />}
            title={t('stalwart.noAccountsTitle')}
            description={t('stalwart.noAccountsDescription')}
          />
        )}

        {!loadingAccounts && accounts.length > 0 && (
          <div className='space-y-1'>
            {accounts.map((acc) => (
              <div
                key={acc.name}
                className='flex items-center justify-between p-2.5 rounded-lg hover:bg-zinc-800/50 cursor-pointer border border-transparent hover:border-zinc-700/50 transition'
                onClick={() => setSelectedAccount(acc)}
              >
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='font-mono text-sm text-white truncate'>{acc.name}</span>
                    <StatusDot color={acc.enabled ? 'green' : 'gray'} />
                  </div>
                  <div className='text-xs text-gray-500 mt-0.5'>
                    {acc.emails.join(', ')} · {acc.quota_human}
                    {acc.description && ` · ${acc.description}`}
                  </div>
                </div>
                <div className='flex items-center gap-1'>
                  {acc.memberOf && acc.memberOf.length > 0 && (
                    <Badge variant='blue' className='text-[9px]'>
                      {acc.memberOf.slice(0, 3).join(', ')}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className='text-xs text-gray-600 text-right'>
          {t('stalwart.totalLabel', { count: accountsData?.total ?? accounts.length })}
        </div>
      </div>
    );
  }

  // ── Tab: Domains ──
  function renderDomains() {
    return (
      <div className='space-y-3'>
        {/* Toolbar */}
        <div className='flex justify-between items-center'>
          <div className='text-sm text-gray-400'>
            {t('stalwart.configuredDomainsHint')}
          </div>
          <Button variant='primary' size='sm' onClick={() => setDomainModalFor('create')}>
            <Plus size={13} /> {t('stalwart.newDomainButton')}
          </Button>
        </div>

        {/* Domains list */}
        {loadingDomains && (
          <div className='py-8 text-center text-sm text-gray-500'>
            <Loader2 className='inline animate-spin mr-2' size={14} />{t('stalwart.loadingDomainsLabel')}
          </div>
        )}

        {!loadingDomains && domains.length === 0 && (
          <EmptyState
            icon={<Globe2 size={32} />}
            title={t('stalwart.noDomainsTitle')}
            description={t('stalwart.noDomainsDescription')}
          />
        )}

        {!loadingDomains && domains.length > 0 && (
          <div className='space-y-1'>
            {domains.map((dom) => (
              <div
                key={dom.name}
                className='flex items-center justify-between p-2.5 rounded-lg hover:bg-zinc-800/50 cursor-pointer border border-transparent hover:border-zinc-700/50 transition'
              >
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='font-mono text-sm text-white truncate'>{dom.name}</span>
                    <StatusDot color={dom.enabled ? 'green' : 'gray'} />
                  </div>
                  {dom.description && (
                    <div className='text-xs text-gray-500 mt-0.5'>{dom.description}</div>
                  )}
                </div>
                <div className='flex items-center gap-1'>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={(e) => {
                      e.stopPropagation();
                      setDnsForDomain(dom.name);
                    }}
                    title={t('stalwart.dnsRecordsTooltip')}
                  >
                    <Shield size={12} /> DNS
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Tab: DNS ──
  function renderDns() {
    // Use hostname or domain from instance
    const dnsDomain = status.domain || status.hostname;
    if (!dnsDomain) {
      return (
        <EmptyState
          icon={<Shield size={32} />}
          title={t('stalwart.domainNotSetTitle')}
          description={t('stalwart.domainNotSetDescription')}
        />
      );
    }

    return (
      <div className='space-y-3'>
        <div className='flex justify-between items-center'>
          <div className='text-sm text-gray-400'>
            {t('stalwart.dnsRecordsForLabel')}{' '}
            <span className='text-white font-mono'>{dnsDomain}</span>
          </div>
          <Button
            variant='primary'
            size='sm'
            onClick={() => setDnsForDomain(dnsDomain)}
          >
            <Shield size={13} /> {t('stalwart.showDnsButton')}
          </Button>
        </div>

        <Card>
          <CardContent className='p-4 space-y-2 text-sm text-gray-400'>
            <p>
              <strong className='text-white'>MX</strong> — {t('stalwart.mxDescription')}
            </p>
            <p>
              <strong className='text-white'>SPF</strong> — {t('stalwart.spfDescription')}
            </p>
            <p>
              <strong className='text-white'>DKIM</strong> — {t('stalwart.dkimDescription')}
            </p>
            <p>
              <strong className='text-white'>DMARC</strong> — {t('stalwart.dmarcDescription')}
            </p>
            <p>
              <strong className='text-white'>SRV</strong> — {t('stalwart.srvDescription')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Tab: Queue ──
  function renderQueue() {
    return (
      <div className='space-y-3'>
        {/* Summary */}
        {queueSummary && (
          <StatsGrid>
            <StatCard label={t('stalwart.statTotal')} value={queueSummary.total_messages} icon={Inbox} />
            <StatCard label={t('stalwart.statPending')} value={queueSummary.pending} icon={Loader2} />
            <StatCard label={t('stalwart.statFailed')} value={queueSummary.failed} icon={XCircle} variant='error' />
            <StatCard label={t('stalwart.statDefer')} value={queueSummary.deferred} icon={RefreshCw} variant='warning' />
          </StatsGrid>
        )}

        {/* Messages */}
        {loadingQueue && (
          <div className='py-8 text-center text-sm text-gray-500'>
            <Loader2 className='inline animate-spin mr-2' size={14} />{t('stalwart.loadingQueueLabel')}
          </div>
        )}

        {!loadingQueue && queue.length === 0 && (
          <EmptyState
            icon={<Inbox size={32} />}
            title={t('stalwart.emptyQueueTitle')}
            description={t('stalwart.emptyQueueDescription')}
          />
        )}

        {!loadingQueue && queue.length > 0 && (
          <div className='space-y-2'>
            {queue.map((msg) => (
              <Card key={msg.id} className='border-zinc-800'>
                <CardContent className='p-3'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <Badge variant='blue' className='text-[9px]'>{msg.id.slice(0, 8)}</Badge>
                        <span className='text-xs text-gray-500'>{msg.created}</span>
                        <span className='text-xs text-gray-500'>{msg.size_human}</span>
                      </div>
                      <div className='text-sm font-mono text-white truncate'>
                        {msg.from}
                      </div>
                      <div className='text-xs text-gray-500 mt-1'>
                        {msg.domains.map((d) => (
                          <span key={d.domain} className='mr-3'>
                            {d.domain}: {t('stalwart.recipientsCountLabel', { count: d.recipient_count })}
                            {d.failed > 0 && (
                              <span className='text-red-400'> · {t('stalwart.failedCountLabel', { count: d.failed })}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className='flex items-center gap-1 ml-2'>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => queueAction(msg.id, 'retry')}
                        title={t('stalwart.retryTooltip')}
                      >
                        <RefreshCw size={12} />
                      </Button>
                      <Button
                        variant='danger'
                        size='icon'
                        onClick={async () => {
                          if (await themedConfirm(t('stalwart.deleteMessageConfirm', { id: msg.id }))) queueAction(msg.id, 'cancel');
                        }}
                        title={t('stalwart.deleteTooltip')}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {queueData && queueData.page > 1 && (
          <div className='flex justify-center gap-2 pt-2'>
            <Button
              variant='ghost'
              size='sm'
              disabled={queuePage <= 1}
              onClick={() => setQueuePage(queuePage - 1)}
            >
              {t('stalwart.prevPageButton')}
            </Button>
            <span className='text-sm text-gray-500 self-center'>
              {t('stalwart.pageLabel', { page: queuePage })}
            </span>
            <Button
              variant='ghost'
              size='sm'
              disabled={queue.length < 20}
              onClick={() => setQueuePage(queuePage + 1)}
            >
              {t('stalwart.nextPageButton')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── Tab: Connection ──
  function renderConnection() {
    if (!connData?.ok) {
      return (
        <EmptyState
          icon={<Settings size={32} />}
          title={t('stalwart.connectionInfoUnavailableTitle')}
        />
      );
    }

    const c = connData;
    return (
      <div className='space-y-4'>
        {/* Admin */}
        <Card>
          <CardContent className='p-4 space-y-2'>
            <div className='flex items-center gap-2 mb-2'>
              <Settings size={14} className='text-blue-400' />
              <span className='text-xs uppercase opacity-60'>{t('stalwart.adminWebLabel')}</span>
            </div>
            {publicAdminUrl
              ? (
                <UrlRow
                  label={t('stalwart.urlPublishedLabel')}
                  url={publicAdminUrl}
                  href={`${publicAdminUrl}/admin/`}
                  isPublic
                />
              )
              : (
                <UrlRow
                  label={t('stalwart.urlInternalOnlyLabel')}
                  url={c.admin.url}
                />
              )}
            <SecretField label={t('stalwart.userLabel')} value={c.admin.user} />
          </CardContent>
        </Card>

        {/* IMAP */}
        <Card>
          <CardContent className='p-4 space-y-2'>
            <div className='flex items-center gap-2 mb-2'>
              <Mail size={14} className='text-emerald-400' />
              <span className='text-xs uppercase opacity-60'>{t('stalwart.imapLabel')}</span>
            </div>
            <UrlRow label={t('stalwart.imapsSslLabel')} url={c.imap.ssl} />
            <div className='text-xs text-gray-500 font-mono'>
              {t('stalwart.imapStartTlsLabel')} {c.imap.starttls}
            </div>
            <div className='text-xs text-gray-400 font-mono'>
              {c.imap.ssl_description}
            </div>
          </CardContent>
        </Card>

        {/* SMTP */}
        <Card>
          <CardContent className='p-4 space-y-2'>
            <div className='flex items-center gap-2 mb-2'>
              <Mail size={14} className='text-amber-400' />
              <span className='text-xs uppercase opacity-60'>{t('stalwart.smtpLabel')}</span>
            </div>
            <div className='text-xs text-gray-500 font-mono'>
              {t('stalwart.submissionStartTlsLabel')} {c.smtp.starttls}
            </div>
            <div className='text-xs text-gray-400 font-mono'>
              {c.smtp.starttls_description}
            </div>
            <div className='text-xs text-gray-500 font-mono'>
              {t('stalwart.smtpsSslLabel')} {c.smtp.ssl}
            </div>
            <div className='text-xs text-gray-400 font-mono'>
              {c.smtp.ssl_description}
            </div>
            <div className='text-xs text-gray-500 font-mono'>
              {t('stalwart.relayInternalLabel')} {c.smtp.relay}
            </div>
          </CardContent>
        </Card>

        {/* JMAP */}
        <Card>
          <CardContent className='p-4 space-y-2'>
            <div className='flex items-center gap-2 mb-2'>
              <Globe2 size={14} className='text-purple-400' />
              <span className='text-xs uppercase opacity-60'>{t('stalwart.jmapLabel')}</span>
            </div>
            <UrlRow label={t('stalwart.jmapApiLabel')} url={c.jmap.url} />
            <UrlRow
              label={t('stalwart.webmailLabel', 'ACM Pošta')}
              url={c.webmail?.path ?? `/mail?instance=${inst.name}`}
              href={c.webmail?.path ?? `/mail?instance=${inst.name}`}
              isPublic
            />
          </CardContent>
        </Card>

        {/* Port overview */}
        <Card>
          <CardContent className='p-4 space-y-2'>
            <div className='text-xs uppercase opacity-60 mb-2'>{t('stalwart.portsLabel')}</div>
            <div className='grid grid-cols-2 gap-2 text-xs font-mono text-gray-400'>
              <div>Admin: :{c.ports.admin}</div>
              <div>SMTP: :{c.ports.smtp}</div>
              <div>Submission: :{c.ports.submission}</div>
              <div>SMTPS: :{c.ports.smtps}</div>
              <div>IMAP: :{c.ports.imap}</div>
              <div>IMAPS: :{c.ports.imaps}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <Button variant='ghost' size='sm' onClick={onClose}>
            ← {t('stalwart.backButton')}
          </Button>
          <div className='flex items-center gap-2'>
            <Mail size={18} className='text-blue-400' />
            <span className='font-bold text-white'>{inst.name}</span>
            <span className={`text-sm ${statusColor}`}>
              {drift ? '⚠ drift' : `${status.db_status} / docker:${status.docker_status}`}
            </span>
          </div>
        </div>
        <div className='flex items-center gap-1'>
          <Button
            variant='primary'
            size='sm'
            onClick={() => navigate('/modules/mail')}
          >
            <Inbox size={12} /> {t('stalwart.openWebmailButton', 'Pošta')}
          </Button>
          {status.docker_status === 'running'
            ? (
              <>
                <Button variant='ghost' size='sm' onClick={() => manageInstance('stop')}>
                  <Square size={12} /> {t('stalwart.stopButton')}
                </Button>
                <Button variant='ghost' size='sm' onClick={() => manageInstance('restart')}>
                  <RefreshCw size={12} /> {t('stalwart.restartButton')}
                </Button>
              </>
            )
            : (
              <Button variant='ghost' size='sm' onClick={() => manageInstance('start')}>
                <Play size={12} /> {t('stalwart.startButton')}
              </Button>
            )}
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setAccountModalFor('credentials')}
          >
            <KeyRound size={12} /> {t('stalwart.credentialsButton')}
          </Button>
        </div>
      </div>

      {/* Prvotní setup wizard ještě nedoběhl — admin API neodpovídá (Stalwart bootstrap mode) */}
      {status.docker_status === 'running' && status.api_health && !status.api_health.ok && (
        <div className='flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3'>
          <AlertCircle size={16} className='text-amber-400 shrink-0 mt-0.5' />
          <div className='text-sm text-amber-200/90'>
            <p className='font-medium text-amber-300'>{t('stalwart.instanceNotSetupTitle')}</p>
            <p className='mt-0.5 text-amber-200/70'>
              {t('stalwart.setupWizardHint', { status: status.api_health.status ?? '?' })}
            </p>
            {publicAdminUrl
              ? (
                <a
                  href={`${publicAdminUrl}/admin/`}
                  target='_blank'
                  rel='noreferrer'
                  className='mt-2 inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 underline'
                >
                  <ExternalLink size={12} /> {t('stalwart.openSetupWizardLink', { url: publicAdminUrl })}
                </a>
              )
              : (
                <p className='mt-2 text-amber-200/70'>
                  {t('stalwart.publishFirstHint')}
                </p>
              )}
          </div>
        </div>
      )}

      {/* Publish admin UI */}
      <PublishPanel
        module='stalwart'
        instance={inst.name}
        port={inst.ports.admin}
        currentDomain={publishEntry?.url ?? null}
        published={publishEntry?.actual ?? false}
        syncing={publishEntry ? publishEntry.desired !== publishEntry.actual : false}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: ['stalwart-status', inst.name] });
          qc.invalidateQueries({ queryKey: ['stalwart-publish-status', inst.name] });
        }}
      />

      {/* Tabs */}
      <Tabs defaultTab={activeTab} onTabChange={(tab) => setActiveTab(tab)}>
        <TabList>
          <Tab id='accounts'>
            <Users size={13} /> {t('stalwart.tabAccounts')}
          </Tab>
          <Tab id='domains'>
            <Globe2 size={13} /> {t('stalwart.tabDomains')}
          </Tab>
          <Tab id='dns'>
            <Shield size={13} /> {t('stalwart.tabDns')}
          </Tab>
          <Tab id='queue'>
            <Inbox size={13} /> {t('stalwart.tabQueue')}
            {queueSummary && queueSummary.failed > 0 && (
              <Badge variant='red' className='ml-1 text-[9px]'>{queueSummary.failed}</Badge>
            )}
          </Tab>
          <Tab id='connection'>
            <Settings size={13} /> {t('stalwart.tabConnection')}
          </Tab>
        </TabList>

        <div className='p-4'>
          <TabPanel id='accounts'>{renderAccounts()}</TabPanel>
          <TabPanel id='domains'>{renderDomains()}</TabPanel>
          <TabPanel id='dns'>{renderDns()}</TabPanel>
          <TabPanel id='queue'>{renderQueue()}</TabPanel>
          <TabPanel id='connection'>{renderConnection()}</TabPanel>
        </div>
      </Tabs>

      {/* Modals */}
      {accountModalFor === 'create' && (
        <AccountModal
          name={inst.name}
          onClose={() => setAccountModalFor(null)}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['stalwart-accounts', inst.name] })}
        />
      )}

      {accountModalFor === 'credentials' && (
        <CredentialsModal
          name={inst.name}
          onClose={() => setAccountModalFor(null)}
        />
      )}

      {domainModalFor === 'create' && (
        <DomainModal
          name={inst.name}
          onClose={() => setDomainModalFor(null)}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['stalwart-domains', inst.name] })}
        />
      )}

      {selectedAccount && (
        <AccountDetailModal
          account={selectedAccount}
          instanceName={inst.name}
          onClose={() => setSelectedAccount(null)}
          onRefresh={() => qc.invalidateQueries({ queryKey: ['stalwart-accounts', inst.name] })}
        />
      )}

      {dnsForDomain && (
        <DnsRecordsModal
          instanceName={inst.name}
          domain={dnsForDomain}
          onClose={() => setDnsForDomain(null)}
        />
      )}
    </div>
  );
}

// ── Instances Tab ─────────────────────────────────────────────────────────

function InstancesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showDeploy, setShowDeploy] = useState(false);
  const [credentialsFor, setCredentialsFor] = useState<string | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<InstanceDetail | null>(null);

  const { data, isLoading } = useQuery<{ ok: boolean; instances?: InstanceDetail[] }>({
    queryKey: ['stalwart-instances'],
    queryFn: () => api.mcp<{ ok: boolean; instances?: InstanceDetail[] }>('stalwart.status', {}),
    refetchInterval: 10_000,
  });

  const instances = data?.instances ?? [];
  const stats = {
    total: instances.length,
    running: instances.filter((i) => i.db_status === 'running' && i.docker_status === 'running').length,
    stopped: instances.filter((i) => i.docker_status === 'stopped').length,
    error: instances.filter((i) => i.db_status === 'error').length,
  };

  async function manageInstance(name: string, action: 'start' | 'stop' | 'restart') {
    const r = await api.mcp<{ ok: boolean; error?: string }>('stalwart.manage', { name, action });
    if (r.ok) toast.success(t('stalwart.instanceActionSuccessToast', { name, action }));
    else toast.error(r.error ?? t('stalwart.actionFailedToast', { action }));
    qc.invalidateQueries({ queryKey: ['stalwart-instances'] });
  }

  async function deleteInstance(name: string) {
    if (!(await themedConfirm(t('stalwart.deleteInstanceConfirm', { name })))) return;
    const r = await api.mcp<{ ok: boolean; error?: string; errors?: string[] }>('stalwart.delete', {
      name,
    });
    if (r.ok) toast.success(t('stalwart.instanceDeletedToast', { name }));
    else toast.error((r.errors ?? [r.error]).join(', '));
    qc.invalidateQueries({ queryKey: ['stalwart-instances'] });
  }

  return (
    <div className='space-y-4'>
      <StatsGrid>
        <StatCard label={t('stalwart.statTotal')} value={stats.total} icon={Server} />
        <StatCard label={t('stalwart.statRunning')} value={stats.running} icon={CheckCircle2} />
        <StatCard label={t('stalwart.statStopped')} value={stats.stopped} icon={Square} />
        <StatCard label={t('stalwart.statError')} value={stats.error} icon={XCircle} variant={stats.error ? 'error' : undefined} />
      </StatsGrid>

      <div className='flex justify-between items-center'>
        <h3 className='text-sm font-semibold text-gray-300'>{t('stalwart.instancesHeading')}</h3>
        <Button variant='primary' onClick={() => setShowDeploy(true)}>
          <Plus size={14} /> {t('stalwart.newInstanceButton')}
        </Button>
      </div>

      {isLoading && (
        <div className='py-8 text-center text-sm text-gray-500'>
          <Loader2 className='inline animate-spin mr-2' size={14} />{t('stalwart.loadingLabel')}
        </div>
      )}

      {!isLoading && instances.length === 0 && (
        <EmptyState
          icon={<Mail size={32} />}
          title={t('stalwart.noInstancesTitle')}
          description={t('stalwart.noInstancesDescription')}
        />
      )}

      {/* Instance detail view */}
      {selectedInstance && (
        <InstanceDetailView
          inst={selectedInstance}
          onClose={() => setSelectedInstance(null)}
        />
      )}

      {/* Instance cards */}
      {!selectedInstance && instances.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
          {instances.map((inst) => (
            <div key={inst.name}>
              <InstanceCard
                inst={inst}
                onView={() => setSelectedInstance(inst)}
                onAction={(action) => manageInstance(inst.name, action)}
                onDelete={() => deleteInstance(inst.name)}
                onCredentials={() => setCredentialsFor(inst.name)}
              />
            </div>
          ))}
        </div>
      )}

      {showDeploy && (
        <DeployModal
          onClose={() => setShowDeploy(false)}
          onDeployed={() => qc.invalidateQueries({ queryKey: ['stalwart-instances'] })}
        />
      )}

      {credentialsFor && (
        <CredentialsModal
          name={credentialsFor}
          onClose={() => setCredentialsFor(null)}
        />
      )}
    </div>
  );
}

// ── Instance Card ─────────────────────────────────────────────────────────

function InstanceCard({
  inst,
  onView,
  onAction,
  onDelete,
  onCredentials,
}: {
  inst: InstanceListItem;
  onView: () => void;
  onAction: (action: 'start' | 'stop' | 'restart') => void;
  onDelete: () => void;
  onCredentials: () => void;
}) {
  const { t } = useTranslation();
  const drift = (inst.db_status === 'running' && inst.docker_status !== 'running');
  const statusColor = drift
    ? 'text-amber-400'
    : inst.db_status === 'running' && inst.docker_status === 'running'
    ? 'text-emerald-400'
    : inst.db_status === 'error'
    ? 'text-red-400'
    : 'text-gray-500';

  return (
    <Card className='border-zinc-800 hover:border-zinc-700/50 transition cursor-pointer' onClick={onView}>
      <CardContent className='p-4'>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <Mail size={16} className='text-blue-400' />
            <span className='font-bold text-white text-sm'>{inst.name}</span>
            <span className={`text-xs ${statusColor}`}>
              {drift ? '⚠ drift' : `${inst.db_status} / docker:${inst.docker_status}`}
            </span>
          </div>
          {inst.image && (
            <Badge variant='blue' className='text-[10px]'>{inst.image.split(':')[1] ?? 'latest'}</Badge>
          )}
        </div>

        {inst.hostname && (
          <div className='mb-3'>
            <UrlRow label={t('stalwart.hostnameLabel')} url={inst.hostname} />
          </div>
        )}

        {/* Porty preview */}
        <div className='space-y-1 mb-3 text-[11px] text-gray-400 font-mono'>
          {inst.ports.admin && <div>admin: 127.0.0.1:{inst.ports.admin} (HTTP)</div>}
          {inst.ports.smtps && <div>SMTPS: {inst.ports.smtps} · IMAPS: {inst.ports.imaps}</div>}
        </div>

        {/* Akce */}
        <div className='flex flex-wrap gap-1.5 pt-2 border-t border-zinc-800/60' onClick={(e) => e.stopPropagation()}>
          {inst.docker_status === 'running'
            ? (
              <Button variant='ghost' onClick={() => onAction('stop')} title={t('stalwart.stopButton')}>
                <Square size={11} /> {t('stalwart.stopButton')}
              </Button>
            )
            : (
              <Button variant='ghost' onClick={() => onAction('start')} title={t('stalwart.startButton')}>
                <Play size={11} /> {t('stalwart.startButton')}
              </Button>
            )}
          <Button variant='ghost' onClick={() => onAction('restart')} title={t('stalwart.restartButton')}>
            <RefreshCw size={11} /> {t('stalwart.restartButton')}
          </Button>
          <Button variant='ghost' onClick={onCredentials} title='Credentials'>
            <Globe2 size={11} /> {t('stalwart.credentialsButton')}
          </Button>
          <Button variant='danger' onClick={onDelete} title={t('stalwart.deleteButton')}>
            <Trash2 size={11} /> {t('stalwart.deleteButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Info Tab ──────────────────────────────────────────────────────────────

function InfoTab() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className='p-4 space-y-3 text-sm text-gray-400'>
        <p>
          <strong className='text-white'>Stalwart Mail Server</strong>{' '}
          {t('stalwart.infoIntroDescription')}
        </p>
        <p>
          {t('stalwart.infoSecondParagraph')}
        </p>
        <div className='grid grid-cols-2 gap-2 text-xs'>
          <div>
            <strong className='text-gray-300'>{t('stalwart.portsColonLabel')}</strong>
            <div className='mt-1 font-mono text-gray-500'>
              admin:8080 · smtp:25 · submission:587
            </div>
            <div className='font-mono text-gray-500'>
              smtps:465 · imap:143 · imaps:993
            </div>
          </div>
          <div>
            <strong className='text-gray-300'>{t('stalwart.apiColonLabel')}</strong>
            <div className='mt-1 font-mono text-gray-500'>
              JMAP, Admin REST
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function StalwartPage() {
  const { t } = useTranslation();
  return (
    <div>
      <PageHeader
        icon={<Mail size={20} />}
        title={t('stalwart.pageTitle')}
        description={t('stalwart.pageDescription')}
      />

      <div className='p-4'>
        <Tabs defaultTab='instances'>
          <TabList>
            <Tab id='instances'>
              <Server size={13} />{t('stalwart.tabInstances')}
            </Tab>
            <Tab id='info'>
              <AlertCircle size={13} />{t('stalwart.tabInfo')}
            </Tab>
          </TabList>
          <TabPanel id='instances'>
            <InstancesTab />
          </TabPanel>
          <TabPanel id='info'>
            <InfoTab />
          </TabPanel>
        </Tabs>
      </div>
    </div>
  );
}
